<template>
  <div>
    <div class="word-card" v-if="similarity">
      <div class="word-title">{{ similarity.word }}发音解决方案一</div>

      <!-- 温馨提示 -->
      <div class="info-panel syllabary-panel">
        <span class="panel-label">🔗 温馨提示</span>
        <span class="panel-text">✂️点击独立音标，可以试听发音，点击字母或者字母组合可以查询字母以及发音一样的单词。</span>
      </div>

      <!-- 字母 ↔ 音标 一一对应 -->
      <div class="info-panel letter-panel" v-if="individualLettersArr.length">
        <span class="panel-label">🎵对应字母 / 🔤独立音标</span>
        <div class="pair-list">
          <div v-for="(letter, i) in individualLettersArr" :key="i" class="pair-col">
            <span class="cell letter-cell" @click="letterTap(i)">{{ letter }}</span>
            <span
              class="cell pho-cell"
              :class="{ 'pho-playing': playingPhone === individualPhoneticsArr[i] }"
              @click="playPhonetic(individualPhoneticsArr[i])"
            >{{ individualPhoneticsArr[i] }}</span>
          </div>
        </div>
      </div>

      <!-- 音素相似单词 -->
      <div class="info-panel result-panel" v-if="individualSimilarWordArr.length">
        <span class="panel-label">🎯 音素相似单词</span>
        <div class="similar-chips">
          <button
            v-for="(item, i) in individualSimilarWordArr"
            :key="i"
            class="similar-btn"
            @click="showWordDetail(i)"
          >
            <span v-for="(c, k) in item.chars" :key="k" class="btn-letter" :class="{ 'btn-letter-hl': c.highlight }">{{ c.ch }}</span>
          </button>
        </div>
      </div>

      <button class="back-btn" @click="$emit('back')">返回单词详解</button>
    </div>

    <div class="word-card empty-card" v-else>
      <span class="empty-text">{{ errorMessage || '暂无相似词信息' }}</span>
    </div>

    <!-- 浮动详情 -->
    <div class="float-tip" v-if="floatingData" @click="clearFloating">
      <div class="float-word">{{ floatingData.word }}</div>
      <div class="float-meaning">{{ floatingData.meaning }}</div>
      <div class="float-meta" v-if="floatingData.grade">
        <span class="float-meta-item">📚 年级：{{ floatingData.grade }}</span>
        <span class="float-meta-item">📖 学期：{{ floatingData.term }}</span>
        <span class="float-meta-item">📗 单元：{{ floatingData.unit }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import request from '../../utils/request'
import { depart, normalizeNewlines, markWordChars } from '../../utils/similar'

export default {
  name: 'IndividualPhonetic',
  data() {
    return {
      similarity: null,
      errorMessage: '',
      individualLettersArr: [],
      individualPhoneticsArr: [],
      individualSimilarWordArr: [],
      floatingData: null,
      playingPhone: ''
    }
  },
  computed: {
    word() {
      return this.$store.state.word
    }
  },
  watch: {
    word(val) {
      if (val && val.trim()) this.fetchSimilarity(val)
    }
  },
  mounted() {
    const word = (this.$store.state.word || '').trim()
    if (word) this.fetchSimilarity(word)
  },
  methods: {
    async fetchSimilarity(word) {
      if (this._fetchingWord === word) return
      this._fetchingWord = word
      try {
        const token = localStorage.getItem('satoken') || ''
        const res = await request.get('/newSimilarity/searchForSimilarity', {
          params: { word, code: token }
        })
        if (res.code === 200) {
          const info = res.NewSimilarity
          if (!info) {
            this.similarity = null
            this.errorMessage = '暂无单词数据'
            return
          }
          const keys = [
            'similarWord', 'individualPhonetics', 'individualLetters',
            'individualPhoneticsChinese', 'individualLettersChinese',
            'syllabaryEnglish', 'syllabaryEnglishLetters'
          ]
          keys.forEach((k) => {
            if (typeof info[k] === 'string') info[k] = normalizeNewlines(info[k])
          })

          this.similarity = info
          this.individualLettersArr = depart(info.individualLetters)
          this.individualPhoneticsArr = depart(info.individualPhonetics)
          this.errorMessage = ''

          if (this.individualLettersArr.length > 0) {
            this.fetchSimilarWord(
              info.word,
              this.individualPhoneticsArr[0] || '',
              this.individualLettersArr[0] || ''
            )
          }
        }
      } catch (e) {
        this.errorMessage = '查询失败，请重新登录！'
        this.similarity = null
      } finally {
        this._fetchingWord = null
      }
    },

    // 点击字母：播放音标 + 查询相似词
    letterTap(index) {
      this.playPhonetic(this.individualPhoneticsArr[index])
      const word = this.$store.state.word
      this.clearFloating()
      this.fetchSimilarWord(word, this.individualPhoneticsArr[index] || '', this.individualLettersArr[index] || '')
    },

    // 查询字母/音标对应的相似单词
    async fetchSimilarWord(word, phone, letter) {
      if (!phone && !letter) return
      try {
        const res = await request.get('/newSimilarity/searchForIndividualSimilarity', {
          params: { word: word || '', letter: letter || '', phone: phone || '' }
        })
        if (res.code === 200) {
          const list = res.IndividualSimilarityResult || []
          const target = (letter || phone || '').toLowerCase()
          const finalList = list
            .map((item) => {
              const { chars, count } = markWordChars(item.word, target)
              return { ...item, chars, targetCount: count }
            })
            .filter((item) => item.targetCount <= 1)
            .map((item) => {
              const rest = { ...item }
              delete rest.targetCount
              return rest
            })
          this.individualSimilarWordArr = finalList
          if (!list.length) this.$message.info('未找到相似单词')
        }
      } catch (e) {
        // 拦截器已提示
      }
    },

    // 播放音标（toggle）
    playPhonetic(phone) {
      const raw = String(phone || '').trim()
      const p = raw.endsWith(':') ? raw.slice(0, -1) + '：' : raw
      if (!p) return

      if (this.playingPhone === p && this._phoneticAudio) {
        this._phoneticAudio.pause()
        this._phoneticAudio = null
        this.playingPhone = ''
        return
      }
      if (this._phoneticAudio) {
        this._phoneticAudio.pause()
        this._phoneticAudio = null
      }
      const audio = new Audio(
        'https://www.sousouwords.com/img/weapp/audios/' + encodeURIComponent(p) + '.mp3'
      )
      this._phoneticAudio = audio
      this.playingPhone = p
      audio.play()
      const reset = () => {
        if (this._phoneticAudio === audio) this._phoneticAudio = null
        if (this.playingPhone === p) this.playingPhone = ''
      }
      audio.onended = reset
      audio.onerror = () => {
        reset()
        this.$message.info('该音标音频缺失')
      }
    },

    // 点击结果单词：播放发音 + 浮动详情
    showWordDetail(index) {
      const item = this.individualSimilarWordArr[index]
      if (!item) return
      this.playWord(item.word)
      this.clearFloating()
      const fullMeaning = normalizeNewlines(item.meaning || '')
      const firstMeaning = fullMeaning ? fullMeaning.split('\n')[0].trim() : '暂无释义'
      this.floatingData = {
        word: item.word || '',
        meaning: firstMeaning || '暂无释义',
        grade: item.grade,
        term: item.term,
        unit: item.unit
      }
      this._floatingTimer = setTimeout(() => this.clearFloating(), 5000)
    },

    playWord(word) {
      if (!word) return
      const audio = new Audio(
        'https://dict.youdao.com/dictvoice?audio=' + encodeURIComponent(word) + '&type=1'
      )
      audio.play()
    },

    clearFloating() {
      if (this._floatingTimer) {
        clearTimeout(this._floatingTimer)
        this._floatingTimer = null
      }
      this.floatingData = null
    }
  }
}
</script>
